import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../../utils';
import { partnerApiPath } from '../../../apiPath';
import { partnerHttpClient } from '../../../httpClient';

const RecruitmentProgressStatusSchema = z
  .enum(['BEFORE_RECRUITMENT', 'RECRUITING', 'IN_PROGRESS', 'COMPLETED'])
  .describe('RecruitmentProgressStatusSchema');

export type RecruitmentProgressStatus = z.infer<typeof RecruitmentProgressStatusSchema>;

const GetRecruitmentCampaignsRequestSchema = z
  .object({
    platform: z.string(),
    progressStatus: RecruitmentProgressStatusSchema,
    title: z.string().nullable().optional(),
    brandIds: z.array(z.string()).nullable().optional(),
    page: z.number().int().min(1).optional(),
    size: z.number().int().min(1).max(100).optional(),
  })
  .describe('GetRecruitmentCampaignsRequestSchema');

export type GetRecruitmentCampaignsRequest = z.infer<typeof GetRecruitmentCampaignsRequestSchema>;

const RecruitmentProgressCountSchema = z
  .object({
    maxParticipants: z.number(),
    appliedCount: z.number(),
    selectedCount: z.number(),
  })
  .describe('RecruitmentProgressCountSchema');

const RecruitmentCampaignSchema = z
  .object({
    campaignId: z.number(),
    brandId: z.string().nullable().optional(),
    goodsImage: z.string(),
    brandName: z.string(),
    campaignTitle: z.string(),
    createdAt: z.string(),
    recruitmentStartAt: z.string().nullable().optional(),
    recruitmentEndAt: z.string().nullable().optional(),
    announcementAt: z.string().nullable().optional(),
    progressStatus: RecruitmentProgressStatusSchema,
    progressCount: RecruitmentProgressCountSchema,
    // 발송 마감일(=선정 발표일 +N영업일 예상값) 및 N. 모든 상태 항상 응답. (SID-4316)
    estimatedShippingDeadlineAt: z.string().nullable().optional(),
    shippingDeadlineDays: z.number().nullable().optional(),
  })
  .describe('RecruitmentCampaignSchema');

export type RecruitmentCampaign = z.infer<typeof RecruitmentCampaignSchema>;

const GetRecruitmentCampaignsResponseDataSchema = z
  .object({
    list: z.array(RecruitmentCampaignSchema),
  })
  .describe('GetRecruitmentCampaignsResponseDataSchema');

const GetRecruitmentCampaignsResponseSchema = createApiResponseSchema(
  GetRecruitmentCampaignsResponseDataSchema,
);

export type GetRecruitmentCampaignsResponse = z.infer<typeof GetRecruitmentCampaignsResponseSchema>;

export const getRecruitmentCampaigns = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetRecruitmentCampaignsRequestSchema),
    async (params: GetRecruitmentCampaignsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetRecruitmentCampaignsResponse>(
          partnerApiPath.getRecruitmentCampaigns,
          params,
        ),
      ),
    validateApiResponse(GetRecruitmentCampaignsResponseSchema),
  ),
);
